import React, { useCallback, useMemo } from 'react';
import * as yup from 'yup';
import Form from '@/common/components/Form/index';
import { TextInput, DatePicker, SelectMeta } from '@/common/components/Inputs/index';
import styles from '@/common/containers/Forms/DecedentMainInfo/styles.less';
import { capitalize } from '@mui/material';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

const DATE_FIELDS = ['dateOfBirth', 'dateOfDeath'];
const MIN_DATE = new Date(1900, 0, 1);

const normalizeDateValue = (value) => {
  if (!value) return null;
  const parsedValue = dayjs(value);
  return parsedValue.isValid() ? parsedValue.toDate() : null;
};

const toIsoDateStart = (value) => {
  if (!value) return '';
  const parsedValue = dayjs(value);
  return parsedValue.isValid()
    ? `${parsedValue.format('YYYY-MM-DD')}T00:00:00`
    : '';
};

const DecedentMainInfo = ({
  initialValues = {},
  className,
  onSubmit,
  formId,
  ...props
}) => {
  const { t } = useTranslation();
  const getDatePart = (value) => {
    if (!value) return '';
    const parsedValue = dayjs(value);
    return parsedValue.isValid() ? parsedValue.format('YYYY-MM-DD') : '';
  };
  const normalizedInitialValues = useMemo(
    () => ({
      ...initialValues,
      ...DATE_FIELDS.reduce(
        (acc, field) => ({
          ...acc,
          [field]: normalizeDateValue(initialValues[field]),
        }),
        {}
      ),
    }),
    [initialValues]
  );
  const validation = useMemo(
    () =>
      yup.object({
        firstName: yup.string().trim().required(t('Enter first name')),
        middleName: yup.string().trim(),
        lastName: yup.string().trim().required(t('Enter last name')),
        socialInsuranceNumber: yup
          .string()
          .trim()
          .required(t('Enter social insurance number')),
        localHealthNumber: yup
          .string()
          .trim()
          .required(t('Enter prov / territ health number')),
        dateOfBirth: yup
          .date()
          .nullable()
          .min(MIN_DATE, t('Date should be on or after 01/01/1900'))
          .required(t('Enter date of birth'))
          .test(
            'afterDeath',
            t('Date of birth should be before death date'),
            function (value) {
              const birthDate = getDatePart(value);
              const deathDate = getDatePart(this.parent.dateOfDeath);
              if (!birthDate || !deathDate) return true;
              return birthDate < deathDate;
            }
          ),
        countryOfBirth: yup.string(),
        cityOfBirth: yup.string().trim().required(t('Enter city')),
        provinceOfBirth: yup.string(),
        dateOfDeath: yup
          .date()
          .nullable()
          .min(MIN_DATE, t('Date should be on or after 01/01/1900'))
          .required(t('Enter date of death'))
          .test(
            'beforeBirth',
            t('Date of death should be after birth date'),
            function (value) {
              const deathDate = getDatePart(value);
              const birthDate = getDatePart(this.parent.dateOfBirth);
              if (!birthDate || !deathDate) return true;
              return deathDate > birthDate;
            }
          ),
        countryOfDeath: yup.string(),
        cityOfDeath: yup.string().trim().required(t('Enter city')),
        provinceOfDeath: yup
          .string()
          .required(t('Enter province / state')),
      }),
    [t]
  );
  const generalInfo = useMemo(
    () => [
      {
        name: 'firstName',
        label: t('First name'),
        required: true,
      },
      {
        name: 'middleName',
        label: t('Middle name'),
      },
      {
        name: 'lastName',
        label: t('Last name'),
        required: true,
      },
      {
        name: 'socialInsuranceNumber',
        label: t('Social insurance number'),
        required: true,
        mask: '000 000 000',
      },
      {
        name: 'localHealthNumber',
        label: t('Prov / territ health number'),
        required: true,
      },
    ],
    [t]
  );
  const birthInfo = useMemo(
    () => [
      {
        name: 'dateOfBirth',
        label: t('Date of birth'),
        required: true,
        Component: DatePicker,
      },
      {
        name: 'countryOfBirth',
        label: t('Country'),
        Component: SelectMeta,
        metaName: 'DD_COUNTRY',
        onChangeFormat: (value) => capitalize(value),
      },
      {
        name: 'cityOfBirth',
        label: t('City'),
        required: true,
        onChangeFormat: (value) => capitalize(value),
      },
      {
        name: 'provinceOfBirth',
        label: t('Province / State'),
        Component: SelectMeta,
        metaName: 'DD_PROV_TERR',
        onChangeFormat: (value) => capitalize(value),
      },
    ],
    [t]
  );
  const deathInfo = useMemo(
    () => [
      {
        name: 'dateOfDeath',
        label: t('Date of death'),
        required: true,
        Component: DatePicker,
      },
      {
        name: 'countryOfDeath',
        label: t('Country'),
        Component: SelectMeta,
        metaName: 'DD_COUNTRY',
      },
      {
        name: 'cityOfDeath',
        label: t('City'),
        required: true,
        onChangeFormat: (value) => capitalize(value),
      },
      {
        name: 'provinceOfDeath',
        label: t('Province / State'),
        Component: SelectMeta,
        metaName: 'DD_PROV_TERR',
      },
    ],
    [t]
  );
  const renderInputs = useCallback(
    (form) =>
      form.map(({ Component, ...input }) =>
        !Component ? (
          <TextInput
            key={input.name}
            className={styles.blockBodyRowItem}
            {...input}
          />
        ) : (
          <Component
            key={input.name}
            className={styles.blockBodyRowItem}
            {...input}
          />
        )
      ),
    []
  );

  return (
    <Form
      className={className}
      validation={validation}
      initialValues={normalizedInitialValues}
      onSubmit={(form) =>
        onSubmit({
          ...form,
          ...DATE_FIELDS.reduce(
            (acc, field) => ({
              ...acc,
              [field]: toIsoDateStart(form[field]),
            }),
            {}
          ),
        })
      }
      id={formId}
      {...props}
    >
      <div className={styles.block}>
        <div className={styles.blockHeader}>
          {t('Decedent’s general info')}
        </div>
        <div className={styles.blockBody}>
          <div className={styles.blockBodyRow}>
            {renderInputs(generalInfo.slice(0, 3))}
          </div>
          <div className={styles.blockBodyRow}>
            {renderInputs(generalInfo.slice(3, 5))}
          </div>
        </div>
      </div>
      <div className={styles.block}>
        <div className={styles.blockHeader}>{t('Decedent’s birth info')}</div>
        <div className={styles.blockBody}>
          <div className={styles.blockBodyRow}>{renderInputs(birthInfo)}</div>
        </div>
      </div>
      <div className={styles.block}>
        <div className={styles.blockHeader}>{t('Decedent’s death info')}</div>
        <div className={styles.blockBody}>
          <div className={styles.blockBodyRow}>{renderInputs(deathInfo)}</div>
        </div>
      </div>
    </Form>
  );
};

export default DecedentMainInfo;
